import { socialNetworkConnectionStatus } from "@/enums/social-network-enums";

export const CONNECT_OUTCOME_SUCCESS = "success";
export const CONNECT_OUTCOME_CANCELLED = "cancelled";
export const CONNECT_OUTCOME_FAILED = "failed";

export const REASON_CODE_TOKEN_EXPIRED = "token_expired";
export const REASON_CODE_CREDENTIALS_REJECTED = "credentials_rejected";
export const REASON_CODE_RENEWAL_FAILED = "renewal_failed";

export const TOKEN_EXPIRY_WARNING_SECONDS = 14 * 86400;

export const STATUS_LABELS = Object.freeze({
  [socialNetworkConnectionStatus.CONNECTED]: "Connected",
  [socialNetworkConnectionStatus.MANUAL_CONNECTION]: "Connected",
  [socialNetworkConnectionStatus.BROKEN_CONNECTION]: "Needs reconnect",
  [socialNetworkConnectionStatus.DISCONNECTED]: "Disconnected",
  [socialNetworkConnectionStatus.NEVER_CONNECTED]: "Not connected",
  [socialNetworkConnectionStatus.INACTIVE]: "Inactive",
});

export const VISIBLE_STATUSES = Object.freeze([
  socialNetworkConnectionStatus.CONNECTED,
  socialNetworkConnectionStatus.MANUAL_CONNECTION,
  socialNetworkConnectionStatus.BROKEN_CONNECTION,
]);

export const LINKED_STATUSES = Object.freeze([
  socialNetworkConnectionStatus.CONNECTED,
  socialNetworkConnectionStatus.MANUAL_CONNECTION,
]);

export const STATUS_ICONS = Object.freeze({
  healthy: { icon: "check-circle", variant: "branded" },
  attention: { icon: "history", variant: "warning" },
  broken: { icon: "exclamation-triangle", variant: "error" },
});

export function isVisibleAccount(account) {
  return VISIBLE_STATUSES.includes(account?.status);
}

export function isLinkedAccount(account) {
  return LINKED_STATUSES.includes(account?.status);
}

export function needsReconnect(account) {
  return account?.status === socialNetworkConnectionStatus.BROKEN_CONNECTION;
}

function formatDate(unixSeconds) {
  return new Date(unixSeconds * 1000).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function networkName(account) {
  return account?.social_network_name || "The network";
}

export function tokenExpiresSoon(account, now = Math.floor(Date.now() / 1000)) {
  const expiresAt = Number(account?.token_expires_at || 0);
  return expiresAt > 0 && expiresAt <= now + TOKEN_EXPIRY_WARNING_SECONDS;
}

function brokenExplanation(account) {
  const network = networkName(account);
  if (account?.status_reason_code === REASON_CODE_TOKEN_EXPIRED) {
    return `${network} signed this account out. This usually happens after a password change, or when access expires (about every 60 days).`;
  }
  return `${network} no longer accepts this connection.`;
}

export function statusPresentation(account, now = Math.floor(Date.now() / 1000)) {
  const status = account?.status;
  const technicalDetails = String(account?.status_reason || "").trim();
  const since = Number(account?.status_changed_at || 0);

  if (status === socialNetworkConnectionStatus.BROKEN_CONNECTION) {
    return {
      label: STATUS_LABELS[status],
      variant: "error",
      reason: brokenExplanation(account),
      detail: since > 0 ? `Since ${formatDate(since)}. Reconnect to keep publishing.` : "Reconnect to keep publishing.",
      technicalDetails,
    };
  }

  if (LINKED_STATUSES.includes(status)) {
    if (account?.status_reason_code === REASON_CODE_RENEWAL_FAILED || technicalDetails) {
      return {
        label: STATUS_LABELS[status],
        variant: "warning",
        reason: `We couldn't renew your ${networkName(account)} access automatically. We'll keep trying every day.`,
        detail: tokenExpiresSoon(account, now)
          ? `If it is still failing by ${formatDate(Number(account.token_expires_at))}, reconnect to avoid an interruption.`
          : "",
        technicalDetails,
      };
    }
    return { label: STATUS_LABELS[status], variant: "success", reason: "", detail: "", technicalDetails: "" };
  }

  return { label: STATUS_LABELS[status] || "Unknown", variant: "standard", reason: "", detail: "", technicalDetails };
}

export function connectionsSummary(accounts) {
  const total = (accounts || []).length;
  if (total === 0) {
    return "Connect the accounts you publish to. We keep them signed in for you and tell you the moment one needs attention.";
  }
  const attention = (accounts || []).filter(account => statusPresentation(account).variant !== "success").length;
  const accountsText = total === 1 ? "1 account" : `${total} accounts`;
  if (attention === 0) {
    return `${accountsText} connected, all healthy.`;
  }
  return `${accountsText} connected · ${attention === 1 ? "1 needs" : `${attention} need`} your attention.`;
}

export function statusIcon(account, now = Math.floor(Date.now() / 1000)) {
  const view = statusPresentation(account, now);
  const handle = account?.account_name ? ` as ${account.account_name}` : "";
  if (view.variant === "error") {
    return { ...STATUS_ICONS.broken, tooltip: `Needs reconnect. ${view.reason}` };
  }
  if (view.variant === "warning") {
    return { ...STATUS_ICONS.attention, tooltip: `Connected${handle}. ${view.reason}` };
  }
  if (view.variant === "success") {
    return { ...STATUS_ICONS.healthy, tooltip: `Connected${handle}` };
  }
  return null;
}

export function connectResultPresentation(result) {
  if (!result || !result.outcome) {
    return null;
  }
  const network = result.network || "The network";
  const reason = String(result.reason || "").trim();

  switch (result.outcome) {
    case CONNECT_OUTCOME_SUCCESS:
      return { variant: "success", title: `${network} connected`, text: `Your ${network} account is linked and ready to publish.` };
    case CONNECT_OUTCOME_CANCELLED:
      return {
        variant: "info",
        title: `${network} connection cancelled`,
        text: `You left the ${network} authorization before it finished. Nothing was changed.`,
      };
    default:
      return {
        variant: "error",
        title: `${network} could not be connected`,
        text: `${network} did not complete the connection. Try again, and contact support if it keeps failing.`,
        technicalDetails: reason,
      };
  }
}

export function apiErrorMessage(error, fallback) {
  const message = error?.response?.data?.message;
  if (typeof message === "string" && message.trim() !== "") {
    return message;
  }
  return fallback;
}
